import { useEffect, useState } from 'react';
import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { db } from '../firebase';
import { thumbnailFor, isViewableContent } from './educationContent';
import { topicById } from '../data/educationTaxonomy';

/*
 * Collections: a series, a curriculum or a reading list. One Firestore document in
 * `education_collections`, shared by all three kinds:
 *
 *   kind         'series' | 'curriculum' | 'reading-list'
 *   title, description
 *   coverURL     optional; when empty the first item's art stands in
 *   topics       [primaryId, ...]   subtopics  []        level  'All Levels' | 'CA-1' | ...
 *   owner        { name, email }    published  boolean
 *   coOwners     [{ name, email }]  shared credit; see educationLifecycle
 *   items        [{ id }]  ordered; ids of education_content documents
 *   curriculum only: preTestId, postTestId (null until assessments exist), requireOrder
 *
 * A trainee sees the kinds differently (a series is low stakes, a curriculum is tracked and
 * tested); the data model is one object so a series can become a curriculum by adding tests.
 */

export const COLLECTION_KINDS = {
  series: { label: 'Series', plural: 'Series', blurb: 'Watch in any order' },
  curriculum: { label: 'Curriculum', plural: 'Curricula', blurb: 'Pre-test, lessons, post-test' },
  'reading-list': { label: 'Reading List', plural: 'Reading Lists', blurb: 'A curated collection of papers and chapters' },
};

export const COLLECTION_LEVELS = ['All Levels', 'Medical Student', 'CA-1', 'CA-2', 'CA-3', 'Fellow', 'Faculty'];

export const kindLabel = (kind) => (COLLECTION_KINDS[kind] || COLLECTION_KINDS.series).label;

const toMillis = (d) => (d && typeof d.toMillis === 'function' ? d.toMillis() : (d ? new Date(d).getTime() : 0));

/** All collections (published only unless asked otherwise), newest first */
export const fetchCollections = async ({ publishedOnly = true } = {}) => {
  const col = collection(db, 'education_collections');
  const snap = await getDocs(publishedOnly ? query(col, where('published', '==', true)) : col);
  const list = [];
  snap.forEach((d) => { const c = { id: d.id, ...d.data() }; if (publishedOnly && c.status === 'retired') return; list.push(c); });
  return list.sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt));
};

/** Published collections that list this topic */
export const fetchCollectionsForTopic = async (topicId) => {
  const snap = await getDocs(query(
    collection(db, 'education_collections'),
    where('published', '==', true),
    where('topics', 'array-contains', topicId),
  ));
  const list = [];
  snap.forEach((d) => { const c = { id: d.id, ...d.data() }; if (c.status !== 'retired') list.push(c); });
  return list.sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt));
};

/** Every content id that belongs to some collection, so topic lists can leave them out */
export const collectedItemIds = (collections) => {
  const ids = new Set();
  (collections || []).forEach((c) => (c.items || []).forEach((it) => ids.add(it.id)));
  return ids;
};

/** The collection's items as content documents, in order, skipping anything missing or hidden */
export const fetchCollectionItems = async (col) => {
  const ids = (col.items || []).map((it) => it.id);
  if (!ids.length) return [];
  const byId = new Map();
  // Firestore allows 30 ids per `in` query
  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(query(collection(db, 'education_content'), where(documentId(), 'in', chunk)));
    snap.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
  }
  return ids.map((id) => byId.get(id)).filter((it) => it && isViewableContent(it));
};

/** Cover image: the uploaded cover, else the first item's art (needs items loaded) */
export const collectionCover = (col, items) => col.coverURL || (items && items[0] ? thumbnailFor(items[0], 640) : null);

export const collectionTopic = (col) => topicById((col.topics || [])[0]) || null;

export const collectionCount = (col) => (col.items || []).length;

/** Published collections, loaded once per mount */
export const useCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    fetchCollections().then((list) => { if (alive) { setCollections(list); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return { collections, loading };
};
